import React, { useState, useEffect, useMemo } from 'react';
import { ChevronUp, ChevronDown, Search, Filter, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useAuth } from '../AuthContext';
import AlertDetailsModal from '../components/AlertDetailsModal'
import '../components/AlertsDetailModal.css'; // Import for hover styling
import './LogsPage.css'

import { useSearchParams } from 'react-router-dom';

const LogsPage = () => {
  // Sample logs data (unchanged)
  const { API_BASE_URL } = useAuth();
  const [logs, setLogs] = useState([]);
  const [alert, setAlert] = useState(null);
  const [asset, setAsset] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showModalAsset, setShowModalAsset] = useState(false);
  const [searchParams] = useSearchParams();
  const [globalSearch, setGlobalSearch] = useState('');

  useEffect(() => {
    const idParam = searchParams.get('id');
    if (idParam) {
      setFilters(prev => ({ ...prev, id: idParam }));
    }
  }, [searchParams]);


  const fetchLogs = () => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}logs`, {
      headers: { Authorization: token },
    })
      .then((res) => res.json())
      .then((data) => {
        setLogs(data);
      })
      .catch((err) => console.error('Error fetching logs:', err));
  };

  const fetchAsset = (log) => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}assets/${log.id}`, {
      headers: { Authorization: token },
    })
      .then((res) => res.json())
      .then((data) => {
        setSelectedAsset(data);
      })
      .catch((err) => console.error('Error fetching logs:', err));

  };
  const fetchAlert = (log) => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}alerts/${log.id}`, {
      headers: { Authorization: token },
    })
      .then((res) => res.json())
      .then((data) => {
        setSelectedLog(data);
      })
      .catch((err) => console.error('Error fetching logs:', err));

  };

  const handleExpandLog = (log) => {
    if (log.category === "alert") {
      fetchAlert(log);
      setShowModal(true);
    }
    else if (log.category === "asset") {
      fetchAsset(log);
      setShowModalAsset(true);
    }
  };

  const onCloseModal = () => {
    setShowModal(false);
  };

  const formatValue = (value) => {
    return Array.isArray(value) ? value.join(", ") : value || "";
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  // Filtering state
  const [filters, setFilters] = useState({
    category: '',
    username: '',
    operation: '',
    id: '',
    timeWindow: {
      start: '',
      end: ''
    }
  });

  // Hover state for quick filter buttons
  const [hoverStates, setHoverStates] = useState({
    id: null,
    username: null,
    category: null,
    operation: null,
    timestamp: null
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage] = useState(50);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });

  // Get unique filter options
  const filterOptions = useMemo(() => ({
    categories: [...new Set(logs.map(log => log.category))],
    usernames: [...new Set(logs.map(log => log.username))],
    operations: [...new Set(logs.map(log => log.operation))]
  }), [logs]);

  // Filtered and sorted logs
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      const logTime = new Date(log.timestamp);
      const startTime = filters.timeWindow.start ? new Date(filters.timeWindow.start) : null;
      const endTime = filters.timeWindow.end ? new Date(filters.timeWindow.end) : null;

      const matchesSearch = globalSearch === '' || 
        log.id.toLowerCase().includes(globalSearch.toLowerCase()) ||
        log.username.toLowerCase().includes(globalSearch.toLowerCase()) ||
        log.operation.toLowerCase().includes(globalSearch.toLowerCase()) ||
        log.category.toLowerCase().includes(globalSearch.toLowerCase());

      return (
        matchesSearch &&
        (filters.category === '' || log.category === filters.category) &&
        (filters.username === '' || log.username === filters.username) &&
        (filters.operation === '' || log.operation === filters.operation) &&
        (filters.id === '' || log.id === filters.id) &&
        (!startTime || logTime >= startTime) &&
        (!endTime || logTime <= endTime)
      );
    });
  }, [logs, filters]);

  // Sorted logs
  const sortedLogs = useMemo(() => {
    let sortableLogs = [...filteredLogs];
    if (sortConfig.key !== null) {
      sortableLogs.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableLogs;
  }, [filteredLogs, sortConfig]);

  // Pagination logic
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = sortedLogs.slice(indexOfFirstLog, indexOfLastLog);

  // Pagination calculations: total pages and page range to display (current ± 3)
  const totalPages = Math.max(1, Math.ceil(sortedLogs.length / logsPerPage));
  const startPage = Math.max(1, currentPage - 3);
  const endPage = Math.min(totalPages, currentPage + 3);
  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  // Change page (clamp within valid range)
  const paginate = (pageNumber) => {
    const clamped = Math.max(1, Math.min(totalPages, pageNumber));
    setCurrentPage(clamped);
  };

  // Ensure currentPage is within range when totalPages change (e.g., filters)
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages]);

  // Sorting handler
  const requestSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Quick filter handler
  const quickFilter = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      category: '',
      username: '',
      operation: '',
      id: '',
      timeWindow: {
        start: '',
        end: ''
      }
    });
    setCurrentPage(1);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };


  return (
    <div className="w-full mx-auto bg-white shadow-md rounded-lg overflow-hidden">
      <div className="d-flex justify-content-between align-items-center p-4 border-bottom">
        <h2 className="mb-0">Logs</h2>
      </div>

      {/* Filtering Section */}
      <div className="p-4 bg-light d-flex align-items-center flex-wrap gap-3">

        {/* Time Window Filter */}
        <div className="relative flex items-center space-x-2">
          <div className="flex items-center">
            <label className="mr-2 text-sm">From:</label>
            <input
              type="date"
              value={filters.timeWindow.start}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                timeWindow: { ...prev.timeWindow, start: e.target.value }
              }))}
              className="p-2 border rounded"
            />
          </div>
          <div className="flex items-center">
            <label className="mr-2 text-sm">To:</label>
            <input
              type="date"
              value={filters.timeWindow.end}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                timeWindow: { ...prev.timeWindow, end: e.target.value }
              }))}
              className="p-2 border rounded"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="relative">
          <select
            className="p-2 border rounded"
            value={filters.category}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
          >
            <option value="">All Categories</option>
            {filterOptions.categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        {/* Username Filter */}
        <div className="relative">
          <select
            className="p-2 border rounded"
            value={filters.username}
            onChange={(e) => setFilters(prev => ({ ...prev, username: e.target.value }))}
          >
            <option value="">All Users</option>
            {filterOptions.usernames.map(username => (
              <option key={username} value={username}>{username}</option>
            ))}
          </select>
        </div>

        {/* Operation Filter */}
        <div className="relative">
          <select
            className="p-2 border rounded"
            value={filters.operation}
            onChange={(e) => setFilters(prev => ({ ...prev, operation: e.target.value }))}
          >
            <option value="">All Operations</option>
            {filterOptions.operations.map(operation => (
              <option key={operation} value={operation}>{operation}</option>
            ))}
          </select>
        </div>
        {/* Clear Filters Button */}
        {(filters.category || filters.username || filters.id || filters.operation ||
          filters.timeWindow.start || filters.timeWindow.end || globalSearch) && (
            <button
              onClick={() => { clearFilters(); setGlobalSearch(''); }}
              className="btn btn-outline-danger d-flex align-items-center"
            >
              <X size={18} className="me-2" /> Clear Filters
            </button>
          )}
      </div>

      {/* Logs Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['timestamp', 'username', 'category', 'operation', 'id'].map((header) => (
                <th
                  key={header}
                  className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => requestSort(header)}
                >
                  <div className="flex items-center">
                    {header.charAt(0).toUpperCase() + header.slice(1)}
                    {sortConfig.key === header && (
                      <span className="ml-2">
                        {sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {currentLogs.map((log) => (
              <tr
                key={`${log.id}-${log.timestamp}`}
                className="border-b hover:bg-gray-50"
                onMouseEnter={() => setHoveredRowId(`${log.id}-${log.timestamp}`)}
                onMouseLeave={() => setHoveredRowId(null)}
              >
                {['timestamp', 'username', 'category', 'operation', 'id'].map((field) => (
                  <td
                    key={field}
                    className="p-3 relative group"
                    onMouseEnter={() => setHoverStates(prev => ({ ...prev, [field]: log[field] }))}
                    onMouseLeave={() => setHoverStates(prev => ({ ...prev, [field]: null }))}
                  >
                    {field === 'id' && (
                      <span
                        onClick={() => handleExpandLog(log)}
                      >
                        {log[field]}
                      </span>
                    )}
                    {field !== 'id' && (
                      <span>
                        {field === 'timestamp' ? formatTimestamp(log[field]) : log[field]}
                      </span>
                    )}
                    {hoverStates[field] === log[field] && hoveredRowId === `${log.id}-${log.timestamp}` && (
                      <button
                        onClick={() => quickFilter(field, log[field])}
                        className="absolute top-0 right-0 m-1 bg-blue-500 text-white rounded-full p-1 hover:bg-blue-600 z-10"
                      >
                        <Filter size={14} />
                      </button>
                    )}
                  </td>
                ))}
                <td className="p-3">
                  <div className="bg-gray-100 p-2 rounded text-xs">
                    {Object.entries(log.data).map(([key, value]) => (
                      <div key={key}>
                        <strong>{key}:</strong> {JSON.stringify(value)}
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && selectedLog && (
        <AlertDetailsModal
          alert={selectedLog}
          onClose={onCloseModal}
          showButtons={false}

        />
      )}

      {showModalAsset && selectedAsset && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Asset Details</h2>
              <button className="btn-close" onClick={() => setShowModalAsset(false)}></button>
            </div>
            <div className="modal-body">
              <p><strong>Host:</strong> {formatValue(selectedAsset.host)}</p>
              <p><span style={{ paddingRight: 10 }}><strong>Device Type:</strong> {formatValue(selectedAsset.profiles.deviceType)}</span>
                <span style={{ paddingRight: 10 }}><strong>Service Type:</strong> {formatValue(selectedAsset.profiles.serviceType)}</span>
                <span><strong>Operating System:</strong> {formatValue(selectedAsset.profiles.operatingSystem)}</span></p>
              <p><strong>Application:</strong> {formatValue(selectedAsset.profiles.application)}</p>
              <p><strong>Application Platform:</strong> {formatValue(selectedAsset.profiles.applicationPlatform)}</p>
            </div>
            <div className="modal-footer">
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center p-4 bg-gray-50">
        <div className="text-sm text-gray-700">
          Showing {indexOfFirstLog + 1} to {Math.min(indexOfLastLog, sortedLogs.length)} of {sortedLogs.length} logs
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            <ChevronLeft size={18} />
          </button>
          {pageNumbers.map((page) => (
            <button
              key={page}
              onClick={() => paginate(page)}
              className={`p-2 border rounded ${currentPage === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'}`}
            >
              {page}
            </button>
          ))}
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogsPage;
